<script setup lang="ts">
import { computed, type Component } from 'vue';

interface Props {
  icon: Component;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | number;
  color?: string;
  ariaLabel?: string;
  ariaHidden?: boolean | string;
  class?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  ariaHidden: false,
});

// Size mapping to Tailwind classes
const sizeClasses = computed(() => {
  const sizeMap = {
    sm: 'w-4 h-4',      // 16px
    md: 'w-6 h-6',      // 24px
    lg: 'w-8 h-8',      // 32px
    xl: 'w-10 h-10',    // 40px
    '2xl': 'w-12 h-12', // 48px
    '3xl': 'w-16 h-16', // 64px
  };

  if (typeof props.size === 'number') {
    const pixelSize = props.size;
    // Convert pixels to Tailwind arbitrary values (4px = 1 unit)
    const tailwindUnits = Math.round(pixelSize / 4);
    return `w-[${tailwindUnits * 4}px] h-[${tailwindUnits * 4}px]`;
  }

  return sizeMap[props.size as keyof typeof sizeMap] || 'w-6 h-6';
});

const iconClass = computed(() => {
  return [
    sizeClasses.value,
    props.color ? '' : 'text-text-primary',
    props.class,
  ].filter(Boolean).join(' ');
});

const iconStyle = computed(() => {
  const style: Record<string, string> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  
  if (props.color) {
    style.color = props.color;
  }
  
  // For custom numeric sizes, use explicit width/height to ensure correct rendering
  if (typeof props.size === 'number') {
    style.width = `${props.size}px`;
    style.height = `${props.size}px`;
  }
  
  return style;
});

const currentIcon = computed(() => props.icon);

const resolvedAriaHidden = computed(() => {
  if (typeof props.ariaHidden === 'boolean') {
    return props.ariaHidden;
  }

  if (props.ariaHidden === 'false') {
    return false;
  }

  if (props.ariaHidden === 'true') {
    return true;
  }

  return Boolean(props.ariaHidden);
});
</script>

<template>
  <component
    :is="currentIcon"
    :class="iconClass"
    :style="iconStyle"
    :aria-label="ariaLabel"
    :aria-hidden="resolvedAriaHidden"
  />
</template>
